<template>
	<div class="toast-wrapper" :class="[cssClass, `toast-${position}`, `toast-${color}`]" :style="style">
		<div class="toast-container">
			<div class="toast-button-group toast-button-group-start">
				<button
					v-for="(btn, index) in startButtons"
					:key="`start-${index}`"
					class="toast-button vfm-focusable vfm-activatable"
					:class="{ 'toast-button-cancel': btn.role === 'cancel' }"
					type="button"
					@click="onClick(btn)"
				>
					<div v-if="!!btn.text" class="toast-button-inner">{{ btn.text }}</div>
					<span
						v-if="!!btn.icon"
						class="toast-button-icon"
						:class="color"
						:style="{ '--icon-url': `url('${btn.icon}')` }"
					></span>
				</button>
			</div>
			<div class="toast-content">
				<div class="toast-message" part="message">
					{{ message }}
				</div>
			</div>
			<div class="toast-button-group toast-button-group-end">
				<button
					v-for="(btn, index) in endButtons"
					:key="`end-${index}`"
					class="toast-button vfm-focusable vfm-activatable"
					:class="{ 'toast-button-cancel': btn.role === 'cancel' }"
					type="button"
					@click="onClick(btn)"
				>
					<div v-if="!!btn.text" class="toast-button-inner">{{ btn.text }}</div>
					<span
						v-if="!!btn.icon"
						class="toast-button-icon"
						:class="color"
						:style="{ '--icon-url': `url('${btn.icon}')` }"
					></span>
				</button>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { ToastButton, ToastColors, ToastPosition, ToastIconSide } from '@/helpers/vfm/types';

const props = withDefaults(
	defineProps<{
		cssClass?: string;
		message?: string;
		duration: number;
		position?: ToastPosition;
		color?: ToastColors;
		buttons: ToastButton[];
	}>(),
	{
		cssClass: '',
		message: '',
		duration: 3000,
		position: 'bottom',
		buttons: () => [],
	}
);
const emits = defineEmits(['dismiss']);

const startButtons = computed(() => props.buttons.filter(btn => btn.side === ToastIconSide.START));
const endButtons = computed(() => props.buttons.filter(btn => !btn.side || btn.side === ToastIconSide.END));

const style = computed(() => {
	if (props.position === 'middle') {
		return {};
	} else {
		return { [props.position]: '8px' };
	}
});

function onClick(btn: ToastButton) {
	if (btn.role === 'cancel') {
		emits('dismiss');
		return;
	}

	if (btn.handler) {
		btn.handler();
	}
}

onMounted(() => {
	if (props.duration > 0) {
		setTimeout(() => {
			emits('dismiss');
		}, props.duration);
	}
});
</script>

<style lang="scss">
@import '~@/styles/variables.scss';

.toast-wrapper {
	display: flex;
	background: var(--jw-color-tertiary);
	margin: auto;
	display: block;
	position: absolute;
	z-index: 10;
	border-radius: 4px;
	left: 8px;
	right: 8px;
	width: auto;
	min-width: auto;
	max-width: 700px;
	height: auto;
	min-height: auto;
	max-height: auto;
	box-shadow: 0 3px 5px -1px rgba(0, 0, 0, 0.2), 0 6px 10px 0 rgba(0, 0, 0, 0.14), 0 1px 18px 0 rgba(0, 0, 0, 0.12);

	&.toast-danger {
		background: #eb445a !important;
	}

	.toast-container {
		display: flex;
		align-items: center;
		pointer-events: auto;
		height: inherit;
		min-height: inherit;
		max-height: inherit;
		contain: content;

		.toast-content {
			display: flex;
			flex: 1 1 0%;
			flex-direction: column;
			justify-content: center;
			padding: 14px 16px;

			.toast-message {
				line-height: 20px;
				font-family: var(--jw-font-family, inherit);
				font-size: 14px;
			}
		}

		.toast-button-group {
			display: flex;
			align-items: center;

			&.toast-button-group-start {
				order: -1;
				margin-left: unset;
				margin-inline-start: 8px;
			}

			&.toast-button-group-end {
				margin-right: unset;
				margin-inline-end: 8px;
			}

			.toast-button {
				padding-left: 15px;
				padding-right: 15px;
				padding-top: 9px;
				padding-bottom: 9px;
				position: relative;
				background-color: transparent;
				font-family: var(--jw-font-family);
				font-size: 14px;
				font-weight: 500;
				letter-spacing: 0.84px;
				text-transform: uppercase;
				overflow: hidden;
				border: 0;
				outline: none;
				z-index: 0;
				cursor: pointer;

				&:not(.toast-button-cancel) {
					color: var(--jw-color-primary) !important;
					&:hover {
						background-color: rgba(var(--jw-color-primary-rgb, 56, 128, 255), 0.08) !important;
					}
				}

				&.toast-button-cancel {
					color: var(--jw-color-tertiary-contrast) !important;
					&:hover {
						background-color: rgba(255, 255, 255, 0.08) !important;
					}
				}

				&:has(> .toast-button-icon) {
					padding: 7px;
					display: flex;
					border-radius: 50%;

					&:hover {
						background-color: rgba(var(--jw-color-primary-rgb, 56, 128, 255), 0.08);
					}

					.toast-button-icon {
						width: 20px;
						height: 20px;
						background-color: var(--jw-color-primary);

						-webkit-mask: var(--icon-url) no-repeat center;
						mask: var(--icon-url) no-repeat center;

						-webkit-mask-size: contain;
						mask-size: contain;

						&.danger {
							background-color: white !important;
						}
					}
				}
			}
		}
	}
}
</style>
